import type { ServiceThread } from "@rakazo/contracts";
import { Button, Spinner } from "@rakazo/ui-web";
import { Download, Play } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { selectedSpaceId, withSpaceHeaders } from "../../lib/rpc";
import { useBoundSpaceId } from "../../lib/space-rpc";

export function CallRecording({
  ticketId,
  thread,
  preview = false,
}: {
  ticketId: string;
  thread: ServiceThread;
  preview?: boolean;
}) {
  const spaceId = useBoundSpaceId() ?? selectedSpaceId();
  const [state, setState] = useState<"idle" | "loading" | "ready" | "error">("idle");
  const [src, setSrc] = useState<string>();
  const request = useRef<AbortController | null>(null);
  const objectUrl = useRef<string | null>(null);
  const audio = useRef<HTMLAudioElement>(null);
  useEffect(() => {
    setState("idle");
    setSrc(undefined);
    return () => {
      request.current?.abort();
      audio.current?.pause();
      if (objectUrl.current) URL.revokeObjectURL(objectUrl.current);
      objectUrl.current = null;
    };
  }, [ticketId, thread.id, spaceId]);
  const recording = thread.recording;
  async function load() {
    if (state === "loading" || recording?.status !== "ready") return;
    const controller = new AbortController();
    request.current = controller;
    setState("loading");
    try {
      const response = preview
        ? await fetch("/fixtures/service-call.wav", { signal: controller.signal })
        : await fetch("/api/customer-service/recording", {
            method: "POST",
            credentials: "include",
            headers: withSpaceHeaders({ "content-type": "application/json" }, spaceId),
            body: JSON.stringify({ ticketId, threadId: thread.id }),
            signal: AbortSignal.any([controller.signal, AbortSignal.timeout(30000)]),
          });
      if (!response.ok || !response.headers.get("content-type")?.startsWith("audio/"))
        throw Error("recording_unavailable");
      const blob = await response.blob();
      if (controller.signal.aborted) return;
      if (blob.size < 44 || blob.size > 19_200_044) throw Error("recording_unavailable");
      const url = URL.createObjectURL(blob);
      objectUrl.current = url;
      setSrc(url);
      setState("ready");
    } catch {
      if (!controller.signal.aborted) setState("error");
    }
  }
  return (
    <section
      className="mb-5 rounded-xl border p-3"
      aria-label={`Recording for ${thread.label}`}
      data-testid={`call-recording-${thread.id}`}
    >
      <div className="flex items-center justify-between gap-3 text-label">
        <span className="min-w-0 truncate">
          {preview
            ? "Sample recording"
            : recording?.status === "ready" && recording.truncated
              ? "Partial recording"
              : "Call recording"}{" "}
          · {thread.label}
        </span>
        {src ? (
          <a
            href={src}
            download="call-recording.wav"
            aria-label="Download recording"
            className="rounded p-1 text-muted-foreground hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring"
          >
            <Download className="size-4" />
          </a>
        ) : null}
      </div>
      {recording?.status === "ready" ? (
        state === "ready" && src ? (
          // biome-ignore lint/a11y/useMediaCaption: The call's transcript is available in the same ticket conversation.
          <audio
            ref={audio}
            src={src}
            controls
            preload="metadata"
            aria-label={`Call recording for ${thread.label}`}
            className="mt-3 w-full"
            onLoadedMetadata={() => {
              void audio.current?.play().catch(() => {});
            }}
          />
        ) : (
          <div className="mt-2 flex items-center gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={() => void load()}
              disabled={state === "loading"}
            >
              {state === "loading" ? <Spinner /> : <Play className="size-4" />}{" "}
              {state === "loading"
                ? "Loading recording"
                : state === "error"
                  ? "Retry recording"
                  : "Play recording"}
            </Button>
            {state === "error" ? (
              <span role="status" className="text-label text-muted-foreground">
                Recording unavailable
              </span>
            ) : null}
          </div>
        )
      ) : (
        <p className="mt-2 text-label text-muted-foreground">
          {recording?.status === "pending" ? "Recording pending" : "Recording unavailable"}
        </p>
      )}
    </section>
  );
}
